import React, { useEffect, useState, useRef } from 'react';
import { Table, Button, Modal, Form, Input, Select, Tag, Space, message, Typography, Upload, List, Avatar, Row, Col, Divider } from 'antd';
import { PlusOutlined, CustomerServiceOutlined, UploadOutlined, PaperClipOutlined, SendOutlined, FilePdfOutlined, SearchOutlined } from '@ant-design/icons';
import { SupportService } from '../api/services/support.service';
import { useAuth } from '../store/auth';
import { useFileViewer } from '../store/FileViewerContext';
import dayjs from 'dayjs';
import jsPDF from 'jspdf';
import SmartMentionsInput from '../components/SmartMentionsInput';
import LinkifyText from '../components/LinkifyText';

const { Title, Text } = Typography;
const { TextArea } = Input;

const normFile = (e) => {
  if (Array.isArray(e)) {
    return e;
  }
  return e?.fileList;
};

const statusColors = {
  new: 'default',
  in_progress: 'warning',
  done: 'success'
};

const statusLabels = {
  new: 'Новое',
  in_progress: 'В работе',
  done: 'Пофиксил'
};

const modules = [
  'Главная', 'Требуется действие', 'Проекты', 'Задачи', 'Календарь',
  'Регулярные совещания', 'Встречи', 'Файлы', 'Чат', 'Почта',
  'Отчёты', 'КСГ', 'ФАО', 'Процессы', 'Сотрудники'
];

export default function SupportPage() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [searchText, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState('');
  const [form] = Form.useForm();
  const { user } = useAuth();
  const { openFileCard } = useFileViewer();
  const pdfRef = useRef();
  const searchInput = useRef(null);

  const isSupportAdmin = user?.username === 'user20' || user?.id === 21;

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const res = await SupportService.getTickets();
      setTickets(res.data);
    } catch (e) {
      message.error('Ошибка загрузки обращений');
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async (ticketId) => {
    try {
      const res = await SupportService.getComments(ticketId);
      setComments(res.data);
    } catch (e) {
      message.error('Ошибка загрузки комментариев');
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !selectedTicket) return;
    
    const textToSubmit = newComment;
    setNewComment('');
    
    try {
      await SupportService.addComment(selectedTicket.id, textToSubmit);
      fetchComments(selectedTicket.id);
    } catch (e) {
      setNewComment(textToSubmit);
      message.error('Ошибка отправки комментария');
    }
  };

  const handleExportPDF = () => {
    if (!selectedTicket) return;
    const doc = new jsPDF({ format: 'a4' });
    doc.addFont('https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Regular.ttf', 'Roboto', 'normal');
    doc.setFont('Roboto');
    doc.setFontSize(16);
    doc.text(`Заявка №${selectedTicket.id} - ${selectedTicket.module_name}`, 15, 20);
    doc.setFontSize(12);
    doc.text(`Заявитель: ${selectedTicket.applicant_name}`, 15, 30);
    doc.text(`Статус: ${statusLabels[selectedTicket.status] || selectedTicket.status}`, 15, 40);
    doc.text(`Дата: ${dayjs(selectedTicket.created_at).format('DD.MM.YYYY HH:mm')}`, 15, 50);
    
    doc.text('Суть проблемы:', 15, 65);
    doc.setFontSize(10);
    const splitDesc = doc.splitTextToSize(selectedTicket.description, 180);
    doc.text(splitDesc, 15, 75);

    let yPos = 75 + splitDesc.length * 5 + 10;

    doc.setFontSize(12);
    doc.text('Комментарии:', 15, yPos);
    yPos += 10;
    doc.setFontSize(10);
    
    comments.forEach(c => {
      const authorLine = `${c.author_name} (${dayjs(c.created_at).format('DD.MM.YYYY HH:mm')}):`;
      doc.text(authorLine, 15, yPos);
      yPos += 5;
      const splitComm = doc.splitTextToSize(c.content, 180);
      doc.text(splitComm, 15, yPos);
      yPos += splitComm.length * 5 + 5;
      if (yPos > 280) {
        doc.addPage();
        yPos = 20;
      }
    });

    doc.save(`Ticket_${selectedTicket.id}.pdf`);
  };

  useEffect(() => {
    if (user) {
      fetchTickets();
    }
  }, [user]);

  const handleCreate = async (values) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const payload = {
        module_name: values.module_name,
        description: values.description
      };
      const res = await SupportService.createTicket(payload);
      const ticket = res.data;

      if (values.file && values.file.length > 0) {
        const fileObj = values.file[0].originFileObj;
        const formData = new FormData();
        formData.append('file', fileObj);
        try {
          await SupportService.uploadTicketFile(ticket.id, formData);
        } catch (uploadErr) {
          message.error('Ошибка при загрузке файла');
        }
      }

      message.success('Обращение создано');
      setModalOpen(false);
      form.resetFields();
      fetchTickets();
    } catch (e) {
      message.error(e.response?.data?.detail || 'Ошибка создания');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusChange = async (id, status) => {
    try {
      await SupportService.updateTicketStatus(id, status);
      message.success('Статус изменен');
      fetchTickets();
    } catch (e) {
      message.error('Ошибка изменения статуса');
    }
  };

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText('');
  };

  const getColumnSearchProps = (dataIndex, title) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Поиск ${title}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Найти
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            Сброс
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              close();
            }}
          >
            Закрыть
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex].toString().toLowerCase().includes((value).toLowerCase())
        : '',
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
  });

  const columns = [
    {
      title: 'Дата',
      dataIndex: 'created_at',
      key: 'created_at',
      sorter: (a, b) => dayjs(a.created_at).valueOf() - dayjs(b.created_at).valueOf(),
      defaultSortOrder: 'descend',
      render: (v) => dayjs(v).format('DD.MM.YYYY HH:mm')
    },
    {
      title: 'Заявитель',
      dataIndex: 'applicant_name',
      key: 'applicant_name',
      ...getColumnSearchProps('applicant_name', 'Заявитель'),
      sorter: (a, b) => (a.applicant_name || '').localeCompare(b.applicant_name || ''),
      render: (v) => v || 'Неизвестно'
    },
    {
      title: 'Модуль',
      dataIndex: 'module_name',
      key: 'module_name',
      filters: modules.map(m => ({ text: m, value: m })),
      onFilter: (value, record) => record.module_name === value,
      sorter: (a, b) => (a.module_name || '').localeCompare(b.module_name || '')
    },
    {
      title: 'Суть проблемы',
      dataIndex: 'description',
      key: 'description',
      ...getColumnSearchProps('description', 'Суть проблемы')
    },
    {
      title: 'Вложение',
      dataIndex: 'file_id',
      key: 'file_id',
      filters: [
        { text: 'Есть', value: 'yes' },
        { text: 'Нет', value: 'no' }
      ],
      onFilter: (value, record) => value === 'yes' ? !!record.file_id : !record.file_id,
      render: (v) => v ? (
        <a onClick={(e) => { e.stopPropagation(); openFileCard(v); }} style={{ cursor: 'pointer' }}>
          <PaperClipOutlined /> Файл
        </a>
      ) : '-'
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      filters: [
        { text: 'Новое', value: 'new' },
        { text: 'В работе', value: 'in_progress' },
        { text: 'Пофиксил', value: 'done' }
      ],
      onFilter: (value, record) => record.status === value,
      render: (v, record) => (
        <Tag color={statusColors[v] || 'default'}>{statusLabels[v] || v}</Tag>
      )
    }
  ];

  if (isSupportAdmin) {
    columns.push({
      title: 'Действия',
      key: 'actions',
      render: (_, record) => (
        <div onClick={(e) => e.stopPropagation()}>
          <Select
            value={record.status}
            onChange={(val) => handleStatusChange(record.id, val)}
            style={{ width: 120 }}
            options={[
              { value: 'new', label: 'Новое' },
              { value: 'in_progress', label: 'В работе' },
              { value: 'done', label: 'Пофиксил' }
            ]}
          />
        </div>
      )
    });
  }

  return (
    <div className="page-container">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 className="page-title">Техподдержка</h1>
          <p className="page-subtitle">Обращения и запросы в службу поддержки</p>
        </div>
        <Button 
          type="primary" 
          icon={<PlusOutlined />} 
          onClick={() => {
            form.resetFields();
            form.setFieldsValue({ applicant_name: user?.full_name });
            setModalOpen(true);
          }}
          style={{ 
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            border: 'none',
            boxShadow: '0 2px 8px rgba(99, 102, 241, 0.3)',
          }}
        >
          Создать обращение
        </Button>
      </div>

      <Card style={{ borderRadius: 16 }} styles={{ body: { padding: 0 } }}>
        <Table
          dataSource={tickets}
          columns={columns}
          rowKey="id"
          loading={loading}
          size="small"
          scroll={{ x: 'max-content' }}
        onRow={(record) => ({
          onClick: () => {
            setSelectedTicket(record);
            fetchComments(record.id);
            setDetailsOpen(true);
          }
        })}
        rowClassName={() => 'clickable-row'}
      />
      </Card>

      <Modal
        title="Детали заявки"
        open={detailsOpen}
        onCancel={() => {
          setDetailsOpen(false);
          setSelectedTicket(null);
        }}
        footer={null}
        width={{ xs: '100%', sm: 800 }}
        style={{ top: 20 }}
      >
        {selectedTicket && (
          <div ref={pdfRef}>
            <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
              <Col>
                <Title level={5} style={{ margin: 0 }}>Заявка #{selectedTicket.id}</Title>
              </Col>
              <Col>
                <Button icon={<FilePdfOutlined />} onClick={handleExportPDF}>Экспорт PDF</Button>
              </Col>
            </Row>
            
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              <Col span={12}>
                <Text type="secondary">Заявитель:</Text> <Text strong>{selectedTicket.applicant_name || 'Неизвестно'}</Text>
              </Col>
              <Col span={12}>
                <Text type="secondary">Дата:</Text> <Text strong>{dayjs(selectedTicket.created_at).format('DD.MM.YYYY HH:mm')}</Text>
              </Col>
              <Col span={12}>
                <Text type="secondary">Модуль:</Text> <Text strong>{selectedTicket.module_name}</Text>
              </Col>
              <Col span={12}>
                <Text type="secondary">Статус:</Text> 
                {isSupportAdmin ? (
                  <Select
                    value={selectedTicket.status}
                    onChange={(val) => {
                      handleStatusChange(selectedTicket.id, val);
                      setSelectedTicket(prev => ({...prev, status: val}));
                    }}
                    style={{ width: 120, marginLeft: 8 }}
                    options={[
                      { value: 'new', label: 'Новое' },
                      { value: 'in_progress', label: 'В работе' },
                      { value: 'done', label: 'Пофиксил' }
                    ]}
                  />
                ) : (
                  <Tag color={statusColors[selectedTicket.status]} style={{ marginLeft: 8 }}>
                    {statusLabels[selectedTicket.status]}
                  </Tag>
                )}
              </Col>
            </Row>

            <div style={{ background: '#f5f5f5', padding: 12, borderRadius: 8, marginBottom: 16 }}>
              <Text strong>Суть проблемы:</Text>
              <div style={{ marginTop: 8, whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                <LinkifyText text={selectedTicket.description} />
              </div>
              {selectedTicket.file_id && (
                <div style={{ marginTop: 12 }}>
                  <Button type="dashed" icon={<PaperClipOutlined />} onClick={() => openFileCard(selectedTicket.file_id)}>
                    Вложение
                  </Button>
                </div>
              )}
            </div>

            <Divider orientation="left">Комментарии</Divider>

            <List
              dataSource={comments}
              renderItem={item => (
                <List.Item>
                  <List.Item.Meta
                    avatar={<Avatar src={item.author_avatar_id ? `/api/v1/files/${item.author_avatar_id}/download` : undefined}>{item.author_name?.[0]}</Avatar>}
                    title={<Space><Text strong>{item.author_name}</Text> <Text type="secondary" style={{ fontSize: 12 }}>{dayjs(item.created_at).format('DD.MM.YYYY HH:mm')}</Text></Space>}
                    description={<div style={{ whiteSpace: 'pre-wrap', color: '#000' }}><LinkifyText text={item.content} /></div>}
                  />
                </List.Item>
              )}
              style={{ maxHeight: 300, overflowY: 'auto', marginBottom: 16 }}
            />

            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 1 }}>
                <SmartMentionsInput
                  value={newComment}
                  onChange={setNewComment}
                  placeholder="Написать комментарий... (используйте @ для упоминания)"
                  autoSize={{ minRows: 2, maxRows: 4 }}
                />
              </div>
              <Button type="primary" icon={<SendOutlined />} onClick={handleAddComment} style={{ height: 'auto' }}>
                Отправить
              </Button>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        title="Новое обращение в техподдержку"
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={submitting}
        okText="Создать"
        cancelText="Отмена"
        width={{ xs: '100%', sm: 640 }}
        style={{ top: 20 }}
      >
        <Form form={form} layout="vertical" onFinish={handleCreate}>
          <Form.Item name="applicant_name" label="Заявитель">
            <Input disabled />
          </Form.Item>
          <Form.Item name="module_name" label="Блок системы" rules={[{ required: true, message: 'Выберите блок' }]}>
            <Select
              showSearch
              placeholder="Выберите проблемный блок"
              options={modules.map(m => ({ value: m, label: m }))}
            />
          </Form.Item>
          <Form.Item name="description" label="Суть проблемы" rules={[{ required: true, message: 'Опишите проблему' }]}>
            <TextArea rows={4} placeholder="Опишите, с какой проблемой вы столкнулись..." />
          </Form.Item>
          <Form.Item name="file" label="Прикрепить файл" valuePropName="fileList" getValueFromEvent={normFile}>
            <Upload beforeUpload={() => false} maxCount={1}>
              <Button icon={<UploadOutlined />}>Выберите файл</Button>
            </Upload>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}